import { useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { getRoleRu } from 'utils/helpers/get/getRoleRu';
import { allRoleArray, superWithoutNewbie } from 'utils/helpers/lists/roleArray';
import Plus from 'assets/icons/plus';

import Filter from 'components/table/filters/filter';
import { documentsStatusList } from 'utils/helpers/lists/documentsStatusList';
import clsx from 'clsx';
import { activeOptions } from 'utils/helpers/constVariables';
import SimpleSelect from 'ui/select';
import { useMeRole } from 'store/hooks/useMeRole';
import { DocumentsStatus, Role } from 'generated/graphql';
import TableFilter from 'components/table/filters';
import Search from 'ui/search';
import { useCustomSearchParams } from 'pages/users/lib/useParams';
import { Table } from '@tanstack/react-table';
import ColumnSettings from '../../../components/table/columnSettings';
import { UserEntityFragment } from 'pages/users/api/fragments/generated/UserEntity.fragment';

interface Props {
  table: Table<UserEntityFragment>;
}

const FacilityUsersFilters = ({ table }: Props) => {
  const location = useLocation();
  const { handleParams, params, setParams, useParamsCount, resetFilter } = useCustomSearchParams();
  const roleParam = params.get('role');
  const documentStatusParam = params.get('documentStatus');
  const activeParam = params.get('active');
  const [role, setRole] = useState(roleParam);
  const [isActive, setIsActive] = useState(activeParam);
  const [documentStatus, setDocumentStatus] = useState<string | null | DocumentsStatus>(documentStatusParam);
  const [showFilter, setShowFilter] = useState(false);

  const paramsCount = useParamsCount();

  const btnRef = useRef<HTMLButtonElement>(null);
  const isMeSupervisor = useMeRole([Role.Supervisor]);
  const isMeAdmin = useMeRole([Role.Admin]);
  const rolesOptions = useMemo(
    () => (isMeSupervisor ? superWithoutNewbie : allRoleArray).map(el => ({ id: el.value, name: el.name })),
    [isMeSupervisor],
  );
  const documentOptions = useMemo(() => documentsStatusList.map(el => ({ id: el.value, name: el.name })), []);

  return (
    <div className="filter-bar flex xs:flex-row flex-col xs:flex-wrap gap-y-2 justify-between">
      <div className="flex flex-col gap-y-2">
        <div className={clsx('flex relative gap-x-2')}>
          <Search />
          <div className={'flex gap-x-0.5'}>
            <button
              type="button"
              className={clsx('btn-stroke_small flex gap-x-1 items-center')}
              ref={btnRef}
              onClick={() => {
                setShowFilter(value => !value);
              }}
            >
              Фильтр
              {Boolean(paramsCount) && (
                <span className="Button3 text-primary rounded-full bg-primary bg-opacity-20 px-[7.5px]">
                  {String(paramsCount)}
                </span>
              )}
            </button>
          </div>
          <ColumnSettings<UserEntityFragment> table={table} />
          <TableFilter show={showFilter} setShow={setShowFilter} triggerRef={btnRef}>
            <SimpleSelect
              label="Роль"
              sort={false}
              onChange={value => {
                setRole(value);
              }}
              value={role}
              placeholder="Не выбрано"
              allOption={'Все'}
              options={rolesOptions}
            />
            <SimpleSelect
              label="Статус документов"
              onChange={value => {
                setDocumentStatus(value);
              }}
              value={documentStatus}
              allOption="Все документы"
              placeholder="Не выбрано"
              options={documentOptions}
            />
            <SimpleSelect
              label="Статус активности"
              onChange={value => {
                setIsActive(value);
              }}
              value={isActive}
              allOption="Все"
              placeholder="Не выбрано"
              options={activeOptions}
            />
            <div className="flex gap-x-5">
              <button
                className="btn-primary"
                onClick={() => {
                  handleParams('role', role);
                  handleParams('documentStatus', documentStatus);
                  handleParams('active', isActive);
                  params.delete('page');
                  setParams(params);
                  setShowFilter(false);
                }}
              >
                Применить
              </button>
              <button
                className="btn-stroke xs:w-auto w-full"
                onClick={() => {
                  setRole(null);
                  setIsActive(null);
                  setDocumentStatus(null);
                  setParams();
                  setShowFilter(false);
                }}
              >
                <span>Сбросить</span>
              </button>
            </div>
          </TableFilter>
        </div>

        {!!paramsCount && (
          <div className="flex gap-2">
            <Filter
              show={!!roleParam}
              content={getRoleRu(roleParam)}
              clickHandler={() => {
                setRole(null);
                resetFilter('role');
              }}
            />
            <Filter
              show={!!documentStatusParam}
              content={documentsStatusList.find(status => status.value === documentStatusParam)?.name}
              clickHandler={() => {
                setDocumentStatus(null);
                resetFilter('documentStatus');
              }}
            />
            <Filter
              show={!!activeParam}
              content={activeParam === 'true' ? 'Активный' : 'Не активный'}
              clickHandler={() => {
                setRole(null);
                resetFilter('active');
              }}
            />
          </div>
        )}
      </div>
      {isMeAdmin && !location.pathname.includes('fired') && (
        <div className="flex self-start">
          <Link to="/users/create">
            <button type="button" className="btn-primary_big flex items-center">
              <span className="inline-flex mr-2">
                <Plus className="text-smena_white" />
              </span>
              Новый пользователь
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default FacilityUsersFilters;
